import { useMemo, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassTable,
  SkeletonList,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { format_date, format_number } from '../../lib/format';
import { useEmployees } from '../../data/use_employees';
import { HOURS_SCALE, useTimeEntries } from '../../data/use_time_entries';
import {
  resolve_employee_name,
  resolve_timesheets_view,
  sort_by_work_date_desc,
} from './timesheet_utils';
import { TimesheetCreateDrawer } from './timesheet_create_drawer';

/**
 * Timesheets list + create-entry drawer (#23312). New WagePro admin surface
 * for capturing hours worked per employee per day, feeding pay-run inclusion
 * downstream. Mirrors `LeaveScreen`'s state-machine + affordance-gating
 * pattern one-for-one — a `create` handler undefined by
 * `useTimeEntries` (read-only OAuth grant, unresolved entity, missing
 * write-capable capability) hides the "Log time" button entirely rather
 * than rendering an affordance that would only 403.
 */
export function TimesheetsScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, entries, retry, create } = useTimeEntries();
  const { employees } = useEmployees();
  const [drawer_open, set_drawer_open] = useState(false);

  const rows = useMemo(() => sort_by_work_date_desc(entries), [entries]);
  const view = resolve_timesheets_view({ state, row_count: rows.length });
  const unknown_employee_label = t('timesheets.unknown_employee');

  return (
    <>
      <ScreenHeader
        title={t('timesheets.title')}
        description={t('timesheets.subtitle')}
        actions={
          create !== undefined ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => set_drawer_open(true)}
            >
              {t('timesheets.log_time')}
            </Button>
          ) : undefined
        }
      />

      {view === 'loading' && (
        <GlassTable>
          <div className="p-4">
            <span className="sr-only">{t('timesheets.loading')}</span>
            <SkeletonList rows={5} />
          </div>
        </GlassTable>
      )}

      {view === 'no_entity' && (
        <EmptyState
          title={t('timesheets.no_entity_title')}
          description={t('timesheets.no_entity_description')}
        />
      )}

      {view === 'entitlement_required' && (
        <EmptyState
          title={t('timesheets.entitlement_required_title')}
          description={t('timesheets.entitlement_required_description')}
        />
      )}

      {view === 'error' && (
        <EmptyState
          title={t('timesheets.load_error_title')}
          description={t('timesheets.load_error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('timesheets.retry')}
            </Button>
          }
        />
      )}

      {view === 'empty' && (
        <EmptyState
          title={t('timesheets.empty_title')}
          description={t('timesheets.empty_description')}
        />
      )}

      {view === 'rows' && (
        <GlassTable>
          <table className="nv-table timesheets-table">
            <thead>
              <tr>
                <th scope="col">{t('timesheets.column_employee')}</th>
                <th scope="col">{t('timesheets.column_work_date')}</th>
                <th scope="col" className="text-center">
                  {t('timesheets.column_hours')}
                </th>
                <th scope="col">{t('timesheets.column_description')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.id}>
                  <td className="font-semibold">
                    {resolve_employee_name(
                      employees,
                      entry.employee_id,
                      unknown_employee_label,
                    )}
                  </td>
                  <td>{format_date(entry.work_date, locale)}</td>
                  <td className="text-center num">
                    {format_number(entry.hours / HOURS_SCALE, locale)}
                  </td>
                  <td>{entry.description ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassTable>
      )}

      {create !== undefined && (
        <TimesheetCreateDrawer
          open={drawer_open}
          onClose={() => set_drawer_open(false)}
          employees={employees}
          onSave={create}
        />
      )}
    </>
  );
}
